import { Icon } from "simplydo/react/ui";

import util from "utils/utils";

import type { CompanyCertification } from "components/innointel/types";

import {
  InfoBoxWidget,
  InfoBoxWidgetTitleArea,
  InfoContainer,
  KeyFactLink,
} from "../Styles";

const certificationMap = {
  cyberEssentials: {
    title: "Cyber Essentials",
    fact: "A self-assessment of your organisation's cyber security.",
    url: "https://www.ncsc.gov.uk/cyberessentials/overview",
  },
  cyberEssentialsPlus: {
    title: "Cyber Essentials Plus",
    fact: "Everything in Cyber Essentials, plus an independent technical audit.",
    url: "https://www.ncsc.gov.uk/cyberessentials/overview",
  },
  PAS2030: {
    title: "PAS 2030",
    fact: "A standard for energy efficiency measures in domestic buildings.",
    url: "https://www.british-assessment.co.uk/services/pas-2030",
  },
  FENSA: {
    title: "FENSA",
    fact: "A government-authorised scheme for replacement windows and doors.",
    url: "https://www.fensa.org.uk/",
  },
  ISO27001: {
    title: "ISO 27001",
    fact: "An international standard for information security management.",
    url: "https://www.iso.org/standard/27001",
  },
  livingWageEmployer: {
    title: "Living Wage Employer",
    fact: "By paying the real Living Wage, employers are voluntarily taking a stand to ensure their employees can earn a wage which is enough to live on.",
    url: "https://www.livingwage.org.uk/",
  },
  ISO9001: {
    title: "ISO 9001",
    fact: "An international standard for quality management systems.",
    url: "https://www.iso.org/iso-9001-quality-management.html",
  },
  SSIP: {
    title: "SSIP",
    fact: "Safety Schemes in Procurement (SSIP) reduce occupational health and safety assessment duplication.",
    url: "https://ssip.org.uk/",
  },
  MCS: {
    title: "MCS",
    fact: "Defines, maintains and improves quality by certifying low-carbon energy technologies and contractors.",
    url: "https://mcscertified.com/",
  },
  gassafe: {
    title: "GasSafe",
    fact: "All gas businesses and their engineers must be on the Gas Safe Register to carry out gas work legally.",
    url: "https://www.gassaferegister.co.uk/",
  },
} as const;

type CertificationKey = keyof typeof certificationMap;

type CertificationKeyFactProps = { certification: CompanyCertification };

const CertificationKeyFact = ({ certification }: CertificationKeyFactProps) => {
  const certificationInfo =
    certification in certificationMap ?
      certificationMap[certification as CertificationKey]
    : {
        title: certification,
        fact: "No information available",
        url: undefined,
      };
  return (
    <KeyFactLink
      title={certificationInfo.title}
      subtitle={certificationInfo.fact}
      url={"url" in certificationInfo ? certificationInfo.url : undefined}
      imageAltContent={
        <Icon name="check" size="large" style={{ marginTop: 10 }} />
      }
      fact={
        "url" in certificationInfo && certificationInfo.url ?
          "Learn more .."
        : ""
      }
    />
  );
};

type CompanyCertificationsProps = { certifications?: CompanyCertification[] };

const CompanyCertifications = ({
  certifications,
}: CompanyCertificationsProps) => {
  if (!certifications?.length) {
    return null;
  }

  return (
    <InfoBoxWidget $sectioned>
      <InfoBoxWidgetTitleArea>
        <Icon name="certificate" />
        <div>
          <h3>Standards & accreditations</h3>
          <span>
            {util.pluralise(
              certifications.length,
              "certification",
              "certifications",
            )}{" "}
            found
          </span>
        </div>
      </InfoBoxWidgetTitleArea>
      <p style={{ color: "grey" }}>
        {util.appName()} has checked this business and found the following
        certifications mentioned on its profile. We recommend double-checking
        compliance before engaging commercially.
      </p>
      <InfoContainer $wrapped>
        {certifications.map((certification) => (
          <CertificationKeyFact
            certification={certification}
            key={certification}
          />
        ))}
      </InfoContainer>
    </InfoBoxWidget>
  );
};

export default CompanyCertifications;
